import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  GraduationCap,
  Mountain,
  Trophy,
  CheckCircle,
  UserPlus,
  UserMinus,
  ChevronRight,
  PartyPopper,
} from "lucide-react";
import { API } from "@/App";
import { formatDateTR } from "@/utils/dateUtils";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

// Event type icons
const EVENT_ICONS = {
  general: Calendar,
  workshop: GraduationCap,
  outdoor: Mountain,
  competition: Trophy,
  social: Users,
};

const EVENT_COLORS = {
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  green: "bg-green-100 text-green-700 border-green-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  pink: "bg-pink-100 text-pink-700 border-pink-200",
};

export default function MyEventsModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "events"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinNote, setJoinNote] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchEvents();
    }
  }, [businessId]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const [upcomingRes, myRes] = await Promise.all([
        fetch(`${API}/events/member/upcoming?business_id=${businessId}`, {
          credentials: "include",
        }),
        fetch(`${API}/events/member/my-events?business_id=${businessId}`, {
          credentials: "include",
        }),
      ]);

      if (upcomingRes.ok) {
        const data = await upcomingRes.json();
        setUpcomingEvents(data);
      }
      if (myRes.ok) {
        const data = await myRes.json();
        setMyEvents(data);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinEvent = async () => {
    if (!selectedEvent) return;

    setJoining(true);
    try {
      const response = await fetch(
        `${API}/events/member/${selectedEvent.event_id}/join`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ note: joinNote }),
        }
      );

      if (response.ok) {
        toast.success(t("events:toast_etkinligeKatiliminizOnaylandi"));
        fetchEvents();
        setShowJoinModal(false);
        setJoinNote("");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveEvent = async (eventId) => {
    if (!confirm(t("events:confirmLeaveEvent"))) return;

    try {
      const response = await fetch(
        `${API}/events/member/${eventId}/leave`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(t('common:leftEvent'));
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const getIconComponent = (type) => {
    return EVENT_ICONS[type] || Calendar;
  };

  const getColorClass = (color) => {
    return EVENT_COLORS[color] || EVENT_COLORS.blue;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Include weekday for events
    const date = new Date(dateStr);
    const weekday = date.toLocaleDateString("tr-TR", { weekday: "long" });
    return `${weekday}, ${formatDateTR(dateStr)}`;
  };

  const renderEventCard = (event, showJoinButton = true) => {
    const IconComp = getIconComponent(event.event_type);
    const isJoined = event.is_joined;
    const isFull = event.is_full;

    return (
      <Card
        key={event.event_id}
        className="border-0 shadow-sm hover:shadow-md transition-shadow"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className={`p-3 rounded-xl ${getColorClass(event.type_color)}`}>
                <IconComp className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-navy">{event.title}</h3>
                  <Badge className={`${getColorClass(event.type_color)} text-xs`}>
                    {event.type_name}
                  </Badge>
                  {isJoined && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Katılıyorsunuz
                    </Badge>
                  )}
                  {isFull && !isJoined && (
                    <Badge variant="secondary" className="text-xs">
                      Dolu
                    </Badge>
                  )}
                </div>

                {event.description && (
                  <p className="text-sm text-grey mt-1 line-clamp-2">
                    {event.description}
                  </p>
                )}

                <div className="flex items-center gap-4 mt-2 text-sm text-grey flex-wrap">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(event.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {event.start_time}
                    {event.end_time && ` - ${event.end_time}`}
                  </span>
                  {event.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {event.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {event.participant_count || 0}
                    {event.max_participants && ` / ${event.max_participants}`}
                  </span>
                </div>

                {!event.is_free && event.price && (
                  <p className="text-sm font-medium text-navy mt-2">
                    Katılım ücreti: {currencySymbol}{event.price}
                  </p>
                )}
              </div>
            </div>

            {showJoinButton && (
              <div className="ml-4">
                {isJoined ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleLeaveEvent(event.event_id)}
                    className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <UserMinus className="w-4 h-4 mr-1" />
                    Ayrıl
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowJoinModal(true);
                    }}
                    disabled={isFull}
                    className="rounded-xl bg-green-600 hover:bg-green-700"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Katıl
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy">Etkinlikler</h1>
        <p className="text-grey text-sm">
          Yaklaşan etkinlikleri görüntüleyin ve katılın
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 rounded-xl">
          <TabsTrigger value="upcoming" className="rounded-xl">
            <Calendar className="w-4 h-4 mr-2" />
            Yaklaşan Etkinlikler ({upcomingEvents.length})
          </TabsTrigger>
          <TabsTrigger value="joined" className="rounded-xl">
            <CheckCircle className="w-4 h-4 mr-2" />
            Katıldıklarım ({myEvents.length})
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Events */}
        <TabsContent value="upcoming" className="mt-4 space-y-4">
          {upcomingEvents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey">{t("events:noUpcomingEvents")}</p>
                <p className="text-sm text-grey mt-1">
                  Yeni etkinlikler burada görünecek
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event) => renderEventCard(event, true))}
            </div>
          )}
        </TabsContent>

        {/* My Events */}
        <TabsContent value="joined" className="mt-4 space-y-4">
          {myEvents.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <PartyPopper className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey">{t("events:noEventParticipation")}</p>
                <Button
                  variant="outline"
                  onClick={() => setActiveTab("upcoming")}
                  className="mt-4 rounded-xl"
                >
                  Etkinliklere Göz At
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myEvents.map((event) => renderEventCard(event, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Join Event Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("events:etkinligeKatil")}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-navy">{selectedEvent.title}</h3>
                <div className="flex items-center gap-3 mt-2 text-sm text-grey">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(selectedEvent.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {selectedEvent.start_time}
                  </span>
                </div>
                {selectedEvent.location && (
                  <p className="flex items-center gap-1 text-sm text-grey mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {selectedEvent.location}
                  </p>
                )}
              </div>

              {!selectedEvent.is_free && selectedEvent.price && (
                <div className="p-3 bg-amber-50 rounded-xl">
                  <p className="text-sm text-amber-800">
                    Bu etkinliğin katılım ücreti <strong>{currencySymbol}{selectedEvent.price}</strong>'dır.
                  </p>
                </div>
              )}

              <div>
                <Label>{t("common:noteOptional")}</Label>
                <Textarea
                  value={joinNote}
                  onChange={(e) => setJoinNote(e.target.value)}
                  placeholder={t("common:ph_organizatoreIletmekIstediginizBirNot")}
                  className="rounded-xl mt-1"
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1 rounded-xl"
                  disabled={joining}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleJoinEvent}
                  className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                  disabled={joining}
                >
                  {joining ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Katılınıyor...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Katıl
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
